import { Bounds } from '@ardoq/graph';
import { AqBlocks, BlocksNode, BlocksVisualState, ItemSet } from '../types';
import { hitTestNode, setNodeCellRect } from '../visual/node';
import { getBlockGrid } from '../placeandroute/blockGrid';
import { createItemSet } from '../itemSet';
import { isGroup, setVisualState } from '../util';
import { NodeHit } from '../visual/types';

export const getColIndex = (group: BlocksNode, pos: number) => {
  if (!isGroup(group) || !group.content || !group.colSize) {
    return -1;
  }

  let colPos = group.content[0];

  if (colPos > pos) {
    return -1;
  }

  for (let ret = 0; ret < group.colSize.length; ++ret) {
    colPos += group.colSize[ret];

    if (colPos > pos) {
      return ret;
    }
  }

  return group.colSize.length;
};

export const getRowIndex = (group: BlocksNode, pos: number) => {
  if (!isGroup(group) || !group.content || !group.rowSize) {
    return -1;
  }

  let rowPos = group.content[1];

  if (rowPos > pos) {
    return -1;
  }

  for (let ret = 0; ret < group.rowSize.length; ++ret) {
    rowPos += group.rowSize[ret];

    if (rowPos > pos) {
      return ret;
    }
  }

  return group.rowSize.length;
};

export const forceVisualState = (
  node: BlocksNode,
  visualState: BlocksVisualState,
  value: boolean
) => {
  const doit = (u: BlocksNode) => {
    setVisualState(u, visualState, value);

    if (u.children) {
      for (const child of u.children) {
        doit(child);
      }
    }
  };

  doit(node);
};

/* stretch the node and its descendancy's cells to fit the specified cell bounds
  so that subsequent transitions start from same positions as those in the
  drag image */
export const forceNodeCell = (
  node: BlocksNode,
  bounds: Bounds,
  reset: boolean
) => {
  if (!node.cell) {
    // node doesn't have a cell to animate from

    return;
  }

  /* build an affine scale/translate transform into s and d to apply to the node and its descendancy */

  const xy = node.cell;
  const uv = bounds;

  const s = [
    (uv[2] - uv[0]) / (xy[2] - xy[0]),
    (uv[3] - uv[1]) / (xy[3] - xy[1]),
  ];
  const d = [-xy[0] * s[0] + uv[0], -xy[1] * s[1] + uv[1]];

  /* apply transform to the node and its descendancy */

  const apply = (node: BlocksNode) => {
    if (node.cell) {
      const oldCell = node.cell;

      const cell: Bounds = [
        node.cell[0] * s[0] + d[0],
        node.cell[1] * s[1] + d[1],
        node.cell[2] * s[0] + d[0],
        node.cell[3] * s[1] + d[1],
      ];

      node.cell = cell;

      if (reset) {
        setNodeCellRect(node, oldCell);
      }
    }

    if (node.children) {
      for (const child of node.children) {
        apply(child);
      }
    }
  };

  apply(node);
};

export const isRoutingChannel = (channel: number) => (channel & 1) === 0;

/* verify that the node can be placed into the specified cell */
export const verifyCell = (node: BlocksNode, cell: Bounds) => {
  if (!node.parent) {
    return false;
  }

  const blockGrid = getBlockGrid(node.parent);

  for (let c = cell[0]; c <= cell[2]; ++c) {
    for (let r = cell[1]; r <= cell[3]; ++r) {
      const block = blockGrid.get(c, r);

      if (block && block !== node) {
        return false;
      }
    }
  }

  return true;
};

export const extendSelected = (component: AqBlocks, items: ItemSet) => {
  const selected = createItemSet(0); // this.selected xor items

  for (const item of items.values()) {
    if (!component.selected.has(item)) {
      selected.add(item);
    }
  }

  for (const item of component.selected.values()) {
    if (!items.has(item)) {
      selected.add(item);
    }
  }

  return selected;
};

export const editCursor = (nodeHit: NodeHit) => {
  switch (nodeHit & NodeHit.Resize) {
    case NodeHit.Right:
    case NodeHit.Left:
      return 'ew-resize';

    case NodeHit.LeftBottom:
    case NodeHit.RightTop:
      return 'nesw-resize';

    case NodeHit.Bottom:
    case NodeHit.Top:
      return 'ns-resize';

    case NodeHit.RightBottom:
    case NodeHit.LeftTop:
      return 'nwse-resize';
  }

  if (nodeHit & NodeHit.Body) {
    return 'move';
  }

  return 'default';
};

export const idleCursor = (component: AqBlocks) => {
  const resolution = 1.0 / component.projection.scale;

  if (!component.isReadonly) {
    for (const node of component.selected.nodes()) {
      const hit = hitTestNode(node, component.pointer, resolution);

      if (hit & NodeHit.Resize) {
        return editCursor(hit);
      }

      if (hit & NodeHit.Body) {
        return editCursor(hit);
      }
    }
  }

  for (const node of component.hovered.nodes()) {
    const hit = hitTestNode(node, component.pointer, resolution);

    if (hit & NodeHit.Expand) {
      return 'default';
    }
  }

  return component.hovered.size === 0 ? 'grab' : 'pointer';
};
